import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import defaultDataAdapters from '../services/defaultDataAdapters';

const WidgetGridContext = createContext(null);
const NOOP = () => {};

/**
 * Provides configuration, callbacks, and data adapters to the entire
 * WidgetGrid tree via React Context.
 *
 * @param {Object}   props
 * @param {Object}   [props.dataAdapters] - Optional object with data-fetching
 *   functions. When omitted, built-in mock adapters are used (ideal for
 *   Storybook / development). In production, pass your own implementations
 *   that call your real API. Each adapter must follow the contract defined in
 *   `services/defaultDataAdapters.js`. You can spread the defaults and
 *   override only the adapters you need:
 *
 *   @example
 *   import { defaultDataAdapters } from '@aimsos/ui';
 *
 *   const myAdapters = {
 *     ...defaultDataAdapters,
 *     fetchWidgetData: myRealFetchWidgetData,
 *   };
 *
 *   <WidgetGridProvider dataAdapters={myAdapters} ...>
 *     <WidgetGrid dashboardUuid="..." />
 *   </WidgetGridProvider>
 */
export const WidgetGridProvider = ({
  scopeType,
  module,
  scopeContext,
  // Legacy props (kept for backward compatibility during migration)
  contextEntity,
  contextId,
  userId = '',
  onNavigate,
  onWidgetAction,
  onCreateNoteClick,
  customWidgetRegistry,
  dataAdapters,
  isEditMode = true,
  children,
}) => {
  const [dashboardUuid, setDashboardUuid] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});

  const updateActiveFilters = useCallback((filters) => {
    setActiveFilters(filters);
  }, []);

  const value = useMemo(
    () => ({
      scopeType: scopeType || null,
      module: module || null,
      scopeContext: scopeContext || null,
      dashboardUuid,
      setDashboardUuid,
      activeFilters,
      updateActiveFilters,
      // Legacy (still used by existing consumers during migration)
      contextEntity: contextEntity || null,
      contextId: contextId ?? null,
      userId,
      onNavigate: onNavigate || NOOP,
      onWidgetAction: onWidgetAction || NOOP,
      onCreateNoteClick: onCreateNoteClick || null,
      customWidgetRegistry: customWidgetRegistry || null,
      dataAdapters: dataAdapters || defaultDataAdapters,
      isEditMode,
    }),
    [
      scopeType, module, scopeContext,
      dashboardUuid, activeFilters, updateActiveFilters,
      contextEntity, contextId, userId,
      onNavigate, onWidgetAction, onCreateNoteClick, customWidgetRegistry, dataAdapters, isEditMode,
    ]
  );

  return (
    <WidgetGridContext.Provider value={value}>
      {children}
    </WidgetGridContext.Provider>
  );
};

export const useWidgetGridContext = () => {
  const context = useContext(WidgetGridContext);
  if (!context) {
    throw new Error(
      'useWidgetGridContext must be used within a WidgetGridProvider'
    );
  }
  return context;
};
